import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  IconButton,
  InputAdornment,
} from '@mui/material';
import AuthBootScreen from '../components/AuthBootScreen';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { buildInfo } from '../config/buildInfo';
import { getSiteName } from '../config/appConfig';
import { useSnackbar } from 'notistack';
import { getApiErrorMessage } from '../utils/apiError';
import axios from 'axios';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, completeTwoFactorLogin, authReady, isAuthenticated } = useAuth();
  const manualLogin = useRef(false);
  const { enqueueSnackbar } = useSnackbar();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [loading, setLoading] = useState(false);
  const verificationInProgress = useRef(false);

  const from = (location.state as any)?.from?.pathname || '/menu-principal';

  useEffect(() => {
    if (authReady && isAuthenticated && !manualLogin.current) {
      navigate('/menu-principal', { replace: true });
    }
  }, [authReady, isAuthenticated, navigate]);

  useEffect(() => {
    const message = (location.state as any)?.message;
    if (message) {
      enqueueSnackbar(message, { variant: 'success' });
    }

    const authRedirectReason = sessionStorage.getItem('authRedirectReason');
    if (authRedirectReason === 'session_expired') {
      enqueueSnackbar('Tu sesion expiro. Inicia sesion nuevamente.', { variant: 'warning' });
      sessionStorage.removeItem('authRedirectReason');
    }
  }, [location.state, enqueueSnackbar]);

  const verifyTwoFactorCode = async (code: string) => {
    if (!challengeToken || code.length !== 6 || verificationInProgress.current) return;
    verificationInProgress.current = true;
    manualLogin.current = true;
    setError('');
    setLoading(true);

    try {
      await completeTwoFactorLogin(challengeToken, code);
      navigate(from, { replace: true });
    } catch (err) {
      manualLogin.current = false;
      if (axios.isAxiosError(err) && !err.response) {
        setError(
          'No se pudo conectar con la API. Verifique que esté en ejecución (dotnet run en eOne.API, puerto 5000) y reinicie npm start si acaba de cambiar la configuración.',
        );
      } else if (axios.isAxiosError(err) && err.response?.status === 401) {
        setError(challengeToken ? 'El código de autenticación no es válido o expiró.' : 'Credenciales inválidas');
      } else {
        setError(getApiErrorMessage(err, challengeToken
          ? 'No se pudo verificar el código de autenticación.'
          : 'Error al iniciar sesión'));
      }
    } finally {
      verificationInProgress.current = false;
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (challengeToken) {
      await verifyTwoFactorCode(twoFactorCode.trim());
      return;
    }

    setError('');
    setLoading(true);
    manualLogin.current = true;
    try {
      const challenge = await login(username, password);
      if (challenge) {
        manualLogin.current = false;
        setChallengeToken(challenge.challengeToken || 'cookie');
        setPassword('');
        setTwoFactorCode('');
        return;
      }
      navigate(from, { replace: true });
    } catch (err) {
      manualLogin.current = false;
      if (axios.isAxiosError(err) && !err.response) {
        setError(
          'No se pudo conectar con la API. Verifique que esté en ejecución (dotnet run en eOne.API, puerto 5000) y reinicie npm start si acaba de cambiar la configuración.',
        );
      } else if (axios.isAxiosError(err) && err.response?.status === 401) {
        setError('Credenciales inválidas');
      } else {
        setError(getApiErrorMessage(err, 'Error al iniciar sesión'));
      }
    } finally {
      setLoading(false);
    }
  };

  const returnToCredentials = () => {
    setChallengeToken(null);
    setTwoFactorCode('');
    setError('');
  };

  if (!authReady || isAuthenticated) {
    return <AuthBootScreen />;
  }

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box
            component="img"
            src="/assets/eone-logo.png"
            alt="eOne"
            sx={{ height: 80, width: 'auto', mb: 2 }}
          />
          <Typography
            component="h1"
            variant="h4"
            sx={{
              mb: 1,
              fontWeight: 600,
              color: 'primary.main',
              letterSpacing: '0.02em',
              fontFamily: [
                '-apple-system',
                'BlinkMacSystemFont',
                '"Segoe UI"',
                'Roboto',
                '"Helvetica Neue"',
                'Arial',
                'sans-serif',
              ].join(','),
            }}
          >
            {getSiteName()}
          </Typography>
          <Typography component="h2" variant="h6" sx={{ mb: 2, color: 'text.secondary', fontWeight: 400 }}>
            {challengeToken ? 'Verificación en dos pasos' : 'Iniciar Sesión'}
          </Typography>
          {challengeToken && (
            <Alert severity="info" sx={{ mt: 1, width: '100%' }}>
              Ingresá el código de 6 dígitos que aparece en tu aplicación autenticadora.
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
              {error}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            {!challengeToken ? (
              <>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="username"
                  label="Usuario o email"
                  name="username"
                  autoComplete="username"
                  autoFocus
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  helperText="Podés iniciar sesión con tu ID de usuario o con tu email"
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Contraseña"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          aria-label={showPassword ? 'ocultar contraseña' : 'mostrar contraseña'}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            ) : (
              <TextField
                margin="normal"
                required
                fullWidth
                autoFocus
                id="two-factor-code"
                name="one-time-code"
                label="Código de autenticación"
                autoComplete="one-time-code"
                value={twoFactorCode}
                onChange={(event) => {
                  const code = event.target.value.replace(/\D/g, '').slice(0, 6);
                  setTwoFactorCode(code);
                  setError('');
                  if (code.length === 6) void verifyTwoFactorCode(code);
                }}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]{6}', maxLength: 6 }}
                helperText="Ingresá el código actual de tu autenticador."
              />
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading || (challengeToken !== null && twoFactorCode.length !== 6)}
            >
              {loading ? 'Verificando…' : challengeToken ? 'Verificar código' : 'Iniciar Sesión'}
            </Button>
            {challengeToken ? (
              <Button fullWidth onClick={returnToCredentials} disabled={loading}>
                Volver al inicio de sesión
              </Button>
            ) : (
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Link
                to="/forgot-password"
                style={{
                  color: 'inherit',
                  textDecoration: 'none',
                  fontSize: '0.875rem'
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: 'primary.main',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  ¿Olvidaste tu contraseña?
                </Typography>
              </Link>
            </Box>
            )}
          </Box>
          <Typography
            variant="caption"
            sx={{
              mt: 2,
              color: 'text.secondary',
              fontSize: '0.7rem',
              opacity: 0.6,
            }}
          >
            v{buildInfo.version} Build {buildInfo.buildNumber}
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login; 